import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./coupon_code.scss";
import logo from "../../assets/fitoja-logo.jpg";
import maleProfilePic from "../../assets/profile_pic_male.jpeg";
import closeIcon from "../../assets/close_icon.png";
import deleteIcon from "../../assets/delete_icon.png";

interface CouponCode {
  _id: string;
  code: string;
  discountPrice: number;
  usageLimit: number;
  usageCount: number;
  isActive: boolean;
  createdAt: Date;
  __v: Number;
}

const CouponCode = () => {
  const [userDetails, setUserDetails] = useState<any>(null);
  const [couponCode, setCouponCodes] = useState<CouponCode[]>([]);
  const [role, setRole] = useState<string>("");
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true); // Add loading state
  const [couponCodeToDelete, setCouponCodeToDelete] =
    useState<CouponCode | null>(null);
  const [addModalVisible, setAddModalVisible] = useState<boolean>(false); // Add CouponCode Modal
  const [newCouponCode, setNewCouponCode] = useState({
    code: "",
    discountPrice: "",
    usageLimit: "",
    expiresAt: "",
  }); // New CouponCode state
  const navigate = useNavigate();
  const isInitialRender = useRef(true);

  // Fetch user details and couponCode
  const fetchUserDetails = async () => {
    try {
      const email = localStorage.getItem("email");
      const token = localStorage.getItem("token");
      if (!email || !token) {
        alert("Authentication failed! Redirecting to login.");
        navigate("/login");
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(
        `https://api.fitoja.in/api/get-employee-details/${email}`,
        config
      );
      setUserDetails(response.data);
      setRole(response.data.role);
      fetchAllCouponCodes();
    } catch (error) {
      console.error("Failed to fetch user details:", error);
      handleLogout();
    }
  };

  const fetchAllCouponCodes = async () => {
    try {
      const response = await axios.get(
        `https://api.fitoja.in/api/get-all-coupon-codes`
      );
      setCouponCodes(response.data.coupons);
    } catch (error) {
      console.error("Failed to fetch couponCode:", error);
    } finally {
      setLoading(false); // Stop the loading indicator once API responds
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("email");
    localStorage.removeItem("token");
    navigate("/login");
  };

  const handleDelete = async () => {
    if (!couponCodeToDelete) return;
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("Authentication failed! Redirecting to login.");
        navigate("/login");
        return;
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      await axios.delete(
        `https://api.fitoja.in/api/delete-coupon-code/${couponCodeToDelete.code}`,
        config
      );
      fetchAllCouponCodes();
      closeDeleteModal();
    } catch (error) {
      console.error("Failed to delete employee:", error);
      alert("Failed to delete employee. Please try again.");
    }
  };

  const openDeleteModal = (couponCode: CouponCode) => {
    setCouponCodeToDelete(couponCode);
    setDeleteModalVisible(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalVisible(false);
    setCouponCodeToDelete(null);
  };

  // Add new coupon code API call
  const handleAddCouponCode = async () => {
    try {
      await axios.post(
        `https://api.fitoja.in/api/create-coupon-code`,
        newCouponCode
      );
      fetchAllCouponCodes();
      closeAddModal();
    } catch (error) {
      console.error("Failed to add employee:", error);
      alert("Failed to add employee. Please try again.");
    }
  };

  const openAddModal = () => {
    setAddModalVisible(true);
  };

  const closeAddModal = () => {
    setAddModalVisible(false);
    setNewCouponCode({
      code: "",
      discountPrice: "",
      usageLimit: "",
      expiresAt: "",
    });
  };

  useEffect(() => {
    if (isInitialRender.current) {
      fetchUserDetails();
      isInitialRender.current = false;
    }
  }, []);

  return (
    <div className="coupon-code">
      {/* Loading indicator */}
      {loading && (
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading, please wait...</p>
        </div>
      )}

      {!loading && (
        <>
          <header className="dashboard-header">
            <div className="logo" onClick={() => navigate("/dashboard")}>
              <img src={logo} />
            </div>
            <div
              className="profile-section"
              onClick={() => setShowOverlay(!showOverlay)}
            >
              <img className="profile-pic" src={maleProfilePic} alt="Profile" />
            </div>
          </header>

          {showOverlay && userDetails && (
            <div className="profile-overlay">
              <div className="overlay-content">
                <img
                  className="close_icon"
                  onClick={() => setShowOverlay(false)}
                  src={closeIcon}
                />
                <p>
                  <strong>Name:</strong> {userDetails.name}
                </p>
                <p>
                  <strong>Email:</strong> {userDetails.email}
                </p>
                <p>
                  <strong>Phone Number:</strong> {userDetails.phoneNumber}
                </p>
                <p>
                  <strong>Role:</strong> {userDetails.role}
                </p>
                <p>
                  <strong>Gender:</strong> {userDetails.gender}
                </p>
                <button className="logout-button" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            </div>
          )}

          <h2 style={{ marginTop: "110px", marginBottom: "0px" }}>
            Coupon Codes
          </h2>

          <div className="create-new-coupon-code" onClick={openAddModal}>
            <span>Create New Coupon Code</span>
          </div>

          <hr></hr>

          <div className="coupon-code-list">
            {couponCode.length > 0 ? (
              couponCode.map((couponCode) => (
                <div key={couponCode._id} className="coupon-code-card">
                  <div className="coupon-code-details">
                    <p>
                      <strong>Code:</strong> <span>{couponCode?.code}</span>
                    </p>
                    <p>
                      <strong>Discount Price:</strong>{" "}
                      <span>{couponCode?.discountPrice}</span>
                    </p>
                    <p>
                      <strong>Usage Limit:</strong>{" "}
                      <span>{couponCode?.usageLimit}</span>
                    </p>
                    <p>
                      <strong>Usage Count:</strong>{" "}
                      <span>{couponCode?.usageCount}</span>
                    </p>
                  </div>
                  <div
                    className="delete-icon"
                    onClick={() => openDeleteModal(couponCode)}
                  >
                    <img className="img" src={deleteIcon}></img>
                  </div>
                </div>
              ))
            ) : (
              <p
                style={{
                  display: "flex",
                  height: "70vh",
                  alignItems: "center",
                }}
              >
                No Coupon Code found
              </p>
            )}
          </div>

          {deleteModalVisible && (
            <div className="delete-modal">
              <div className="delete-modal-content">
                <h2>Delete Confirmation</h2>
                <p>Are you sure you want to delete this coupon code?</p>
                <span style={{ display: "flex", gap: "20px" }}>
                  <button onClick={closeDeleteModal} className="cancel-button">
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    className="confirm-delete-button"
                  >
                    Yes, Delete
                  </button>
                </span>
              </div>
            </div>
          )}

          {addModalVisible && (
            <div className="add-modal">
              <div className="add-modal-content">
                <h2>Create Coupon Code</h2>
                <form>
                  <input
                    type="text"
                    placeholder="Code"
                    value={newCouponCode.code}
                    onChange={(e) =>
                      setNewCouponCode({
                        ...newCouponCode,
                        code: e.target.value,
                      })
                    }
                    required
                  />
                  <input
                    type="number"
                    placeholder="Discount Price"
                    value={newCouponCode.discountPrice}
                    onChange={(e) =>
                      setNewCouponCode({
                        ...newCouponCode,
                        discountPrice: e.target.value,
                      })
                    }
                    required
                  />
                  <input
                    type="text"
                    placeholder="Usage Limit"
                    value={newCouponCode.usageLimit}
                    onChange={(e) =>
                      setNewCouponCode({
                        ...newCouponCode,
                        usageLimit: e.target.value,
                      })
                    }
                    required
                  />
                  {/* <input
                    type="text"
                    placeholder="Expiry Date (Optional)"
                    value={newCouponCode.expiresAt}
                    onChange={(e) =>
                      setNewCouponCode({
                        ...newCouponCode,
                        expiresAt: e.target.value,
                      })
                    }
                    required
                  /> */}
                  <button
                    type="button"
                    onClick={handleAddCouponCode}
                    className="coupon-code-button"
                  >
                    Create Coupon Code
                  </button>
                  <button
                    type="button"
                    onClick={closeAddModal}
                    className="cancel-button"
                  >
                    Cancel
                  </button>
                </form>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CouponCode;
